import { useCallback, useMemo, useRef } from 'react';
import { z } from 'zod';

import { isSupportedCountry } from 'src/legacy/components/Address/utils/countryHelpers';

const useAddressValidation = (initialAddressLine1: string) => {
  const initialAddressLine1Ref = useRef(initialAddressLine1);

  /**
   * Used to determine if only the fullAddress input field should be shown.
   * Happens when the selected country isn't supported for google maps autocomplete
   * or a fullAddress exists without addressLine1 (possible through csv import)
   */
  const isOnlyFullAddress = useCallback(
    ({
      country = '',
      fullAddress = '',
    }: {
      country?: string;
      fullAddress?: string;
    }) =>
      !isSupportedCountry(country) ||
      (!initialAddressLine1Ref.current && fullAddress !== ''),
    [],
  );

  const addressFieldSchema = useMemo(() => {
    return z
      .object({
        country: z.string().optional(),
        fullAddress: z.string().optional(),
        region: z.string().optional(),
        postalCode: z.string().optional(),
        addressLine1: z.string().optional(),
        city: z.string().optional(),
        addressLine2: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          isOnlyFullAddress({
            country: data.country,
            fullAddress: data.fullAddress,
          })
        ) {
          // Only fullAddress required
          if (!data.fullAddress) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Address is required',
              path: ['fullAddress'],
            });
          }
          if (!data.country) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Country is required',
              path: ['country'],
            });
          }
          // No other required fields
        } else {
          // Standard address required fields
          if (!data.country) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Country is required',
              path: ['country'],
            });
          }
          if (data.country === 'GB') {
            // region optional
          } else {
            if (!data.region) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'State is required',
                path: ['region'],
              });
            }
          }
          if (data.country === 'US') {
            if (!data.postalCode) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Zip code is required',
                path: ['postalCode'],
              });
            }
          } else {
            if (!data.postalCode) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Postal code is required',
                path: ['postalCode'],
              });
            }
          }
          if (!data.addressLine1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Address line 1 is required',
              path: ['addressLine1'],
            });
          }
          if (!data.city) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'City is required',
              path: ['city'],
            });
          }
        }
      });
  }, [isOnlyFullAddress]);

  return { isOnlyFullAddress, addressFieldSchema } as const;
};

export default useAddressValidation;
